/**
 * PropertyBar - Visual bar chart for 1-10 property ratings
 *
 * Displays a horizontal bar with fill proportional to the value.
 * Used in ProductDetailModal and ComparisonPowerBars.
 */

interface PropertyBarProps {
  /** Property label (e.g., "Warmth", "Durability") */
  label: string;
  /** Value from 1-10 */
  value: number;
  /** Maximum value for scale (default: 10) */
  max?: number;
  /** Optional color variant */
  variant?: 'default' | 'highlight' | 'comparison';
  /** Show numeric value next to bar */
  showValue?: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
}

/** Get color classes based on value and variant */
function getBarColorClasses(value: number, max: number, variant: PropertyBarProps['variant']): string {
  const percentage = (value / max) * 100;

  if (variant === 'highlight') {
    return 'bg-orange-500';
  }

  if (variant === 'comparison') {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  }

  // Default: gradient based on value
  if (percentage >= 80) return 'bg-green-500';
  if (percentage >= 60) return 'bg-cyan-500';
  if (percentage >= 40) return 'bg-amber-500';
  return 'bg-neutral-500';
}

export function PropertyBar({
  label,
  value,
  max = 10,
  variant = 'default',
  showValue = true,
  size = 'md',
}: PropertyBarProps) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  const barColorClass = getBarColorClasses(value, max, variant);
  const heightClass = size === 'sm' ? 'h-1.5' : 'h-2';

  return (
    <div className="flex items-center gap-3">
      <span
        className={`text-neutral-400 ${size === 'sm' ? 'text-xs w-24' : 'text-sm w-28'} truncate`}
      >
        {label}
      </span>
      <div className={`flex-1 ${heightClass} bg-neutral-800 rounded-full overflow-hidden`}>
        <div
          className={`${heightClass} ${barColorClass} rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-label={`${label}: ${value} out of ${max}`}
        />
      </div>
      {showValue && (
        <span className={`font-mono text-neutral-300 ${size === 'sm' ? 'text-xs w-6' : 'text-sm w-8'} text-right`}>
          {value}
        </span>
      )}
    </div>
  );
}

/**
 * PropertyBarGroup - Display multiple property bars together
 */
interface PropertyBarGroupProps {
  /** Map of property names to values */
  properties: Record<string, number | undefined>;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Maximum properties to show (default: all) */
  maxVisible?: number;
}

/** Format property key to readable label */
function formatPropertyLabel(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function PropertyBarGroup({ properties, size = 'md', maxVisible }: PropertyBarGroupProps) {
  // Filter out undefined values and sort by value descending
  const entries = Object.entries(properties)
    .filter((entry): entry is [string, number] => entry[1] !== undefined)
    .sort((a, b) => b[1] - a[1]);

  const visibleEntries = maxVisible ? entries.slice(0, maxVisible) : entries;

  if (visibleEntries.length === 0) {
    return (
      <p className="text-neutral-500 text-sm">No property ratings available</p>
    );
  }

  return (
    <div className={`space-y-${size === 'sm' ? '2' : '3'}`}>
      {visibleEntries.map(([key, value]) => (
        <PropertyBar
          key={key}
          label={formatPropertyLabel(key)}
          value={value}
          size={size}
        />
      ))}
    </div>
  );
}
